import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommandService } from '@core/services/command.service';
import { EliteFormFlowChartDataService } from '@features/elite-form/services/elite-form-flow-chart-data.service';
import { EliteFormManagerService } from '@features/elite-form/services/elite-form-manager.service';
import { EliteFormServiceFactory } from '@features/elite-form/services/elite-form-service-factory.service';
import { EliteFormValidationService } from '@features/elite-form/services/form-validation.service';
import { DialogDefinitionEditorBase, PlatformDialog } from '@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models';
import { ValidatorHelper } from '@shared/components/platform-tool/platform-tool-dialog-definition/models/validators-model';
import { takeUntil } from 'rxjs';
import {getCurrentPlatformLink, ParticleState} from '@shared/store/reducers/particle.reduer';
import {Store} from '@ngrx/store';
import {MagicLinkModel} from '@shared/models/platform-tool-models';
import {FormResponseViewService} from "@features/elite-form/services/form-response-view.service";
import {filter} from "rxjs/operators";

@Component({
    standalone: false,
    selector: 'app-dialog-filed-elite-form-group-editor',
  templateUrl: './dialog-filed-elite-form-group-editor.component.html',
  styleUrl: './dialog-filed-elite-form-group-editor.component.scss'
})
export class DialogFiledEliteFormGroupEditorComponent extends DialogDefinitionEditorBase implements OnInit, OnDestroy {
  eliteFormId: string = '';
  inputForm: FormGroup = new FormGroup({});
  childFlowchartId: string;
  firstTimeLoading = false;
  pages: any;
  platformDialogs = {};
  itemIds = [];
  eliteFormManagerService: EliteFormManagerService;
  private currentPlatformLink: MagicLinkModel;

  constructor(
    private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
    private eliteFormServiceFactory: EliteFormServiceFactory,
    private formResponseViewService: FormResponseViewService,
    private commandService: CommandService,
    private particleStore: Store<ParticleState>,
    private validationService: EliteFormValidationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.particleStore.select(getCurrentPlatformLink).pipe(takeUntil(this.unSubscribe$)).subscribe((platformLink: any) => {
      this.currentPlatformLink = platformLink;
    });
    super.ngOnInit();


    this.eliteFormId = this.dialogField.fieldValues['EliteFormId'];
    this.childFlowchartId = this.dialogField.fieldValues['ChildFlowchartId'];
    this.eliteFormManagerService = this.eliteFormServiceFactory.getEliteFormManagerService(this.eliteFormId);

    // Subscribe to validation changes
    this.validationService.getControlValidationState(this.dialogField.key)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(isValid => {
        if (!isValid) {
          this.errorMessage = this.getErrorMessage();
        } else {
          this.errorMessage = null;
        }
        this.validationChange.emit(isValid);
      });

    // Update form control value when platformDialogs changes
    this.getPlatformDialogsByFlowchartId();

    this.eliteFormFlowChartDataService.responseInvalidateSubject
      .pipe(takeUntil(this.unSubscribe$), filter(eId => eId === this.eliteFormId))
      .subscribe(() => {
        this.getPlatformDialogsByFlowchartId();
      });
  }

  private getErrorMessage(): string {
    const errors = this.validationService.getErrors(this.dialogField.key);
    if (!errors) return '';

    return this.validationService.getErrorMessage(
      this.dialogField.key,
      'RequiredEliteFormGroup',
      null,
      { '[FIELD_TITLE]': this.dialogField.title }
    );
  }

  private getPlatformDialogsByFlowchartId() {
    // Update with IsClientSchema from currentPlatformLink if needed
    this.dialogField.fieldValues = {
      ...this.dialogField.fieldValues,
      IsClientSchema: this.currentPlatformLink.fieldValues['IsClientSchema']
    };

    // Use the service method
    this.formResponseViewService.getPlatformDialogsByFlowchartId(
      this.eliteFormId,
      null,
      this.childFlowchartId,
      this.dialogField.fieldValues['SourceItemId'],
      this.dialogField.key,
      this.dialogField.fieldValues,
      this.currentPlatformLink.fieldValues['IsTranslated'],
      true // child group: render from PlatformDialogs only, skip the redundant pages query
    ).pipe(takeUntil(this.unSubscribe$)).subscribe(response => {
      this.pages = response.fieldValues['EliteFormPages'] || this.pages;
      this.platformDialogs = response.fieldValues['PlatformDialogs'];
      this.itemIds = Object.keys(this.platformDialogs);

      // Update validation
      this.validationService.setValue(
        this.dialogField.key,
        this.platformDialogs,
        this.platformDialog.id,
        { emitEvent: this.firstTimeLoading }
      );
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  copySection() {
    const autoSaveCommand = this.dialogField.fieldValues['Commands'][0];
    autoSaveCommand.fieldValues['ResponseId'] = this.eliteFormFlowChartDataService.responseId;
    autoSaveCommand.fieldValues['SchemaId'] = this.eliteFormFlowChartDataService.schemaId;
    autoSaveCommand.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;
    this.commandService.executePlatformCommand(autoSaveCommand)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(response => {
        this.eliteFormFlowChartDataService.refreshResponseItems(this.eliteFormId);
        this.firstTimeLoading = true;
        this.formResponseViewService.updateTableOfContents();
      });
  }

  onDeleteSection(value: boolean) {
    if (value) this.firstTimeLoading = true;
  }
}
